import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { TextField, Tooltip, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Handle, Position } from "reactflow";
import { HandleDot } from "../handle";
import { useTheme } from "../../../contexts/ThemeContext";
import { getData } from "../../../apis/api";
import { ENDPOINTS } from "../../../apis/urls";

export const SendEmailEditModal = ({ open, handleClose, data }) => {
  const { darkMode } = useTheme();
  const isEdit = !!data?.id;

  const currentConfig = data?.data?.config || {};
  const [templateId, setTemplateId] = useState(currentConfig.templateId || "");
  const [toSource, setToSource] = useState(currentConfig.toSource || "contact_email");
  const [toVariable, setToVariable] = useState(currentConfig.toVariable || "");
  const [toLiteral, setToLiteral] = useState(currentConfig.toLiteral || "");

  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (!open) return;
    getData(ENDPOINTS.WORKFLOW.EMAIL_TEMPLATES)
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.Content || [];
        setTemplates(list);
        if (!templateId && list.length > 0) {
          setTemplateId(list[0].id);
        }
      })
      .catch(() => setTemplates([]));
  }, [open]);

  useEffect(() => {
    if (data?.data?.config) {
      const cfg = data.data.config;
      setTemplateId(cfg.templateId || "");
      setToSource(cfg.toSource || "contact_email");
      setToVariable(cfg.toVariable || "");
      setToLiteral(cfg.toLiteral || "");
    }
  }, [data]);

  const onSave = () => {
    const selectedTemplate = templates.find((t) => t.id === templateId);
    const config = {
      templateId: Number(templateId) || 0,
      toSource,
      toVariable: toSource === "variable" ? toVariable : undefined,
      toLiteral: toSource === "literal" ? toLiteral : undefined,
    };
    handleClose(
      {
        config,
        templateName: selectedTemplate?.name || `Template #${templateId}`,
      },
      isEdit
    );
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 480,
    maxWidth: "95vw",
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: darkMode ? "#18181b" : "#ffffff",
    border: `1px solid ${darkMode ? "#27272a" : "#e2e8f0"}`,
    borderRadius: 3,
    boxShadow: 24,
    p: 3.5,
  };

  return (
    <Modal open={open} onClose={() => handleClose(null)}>
      <Box sx={modalStyle}>
        <div className="flex items-center gap-2 mb-2">
          <div className="p-2 rounded-lg bg-amber-500/10 text-amber-500">
            <EmailIcon />
          </div>
          <div>
            <h2 className={`text-lg font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>
              {isEdit ? "Edit Send Email Node" : "Configure Send Email"}
            </h2>
            <p className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
              Sends a templated HTML email to the contact or specified recipients.
            </p>
          </div>
        </div>

        <div className="space-y-4 my-4">
          <FormControl fullWidth size="small">
            <InputLabel id="email-tpl-label">Email Template *</InputLabel>
            <Select
              labelId="email-tpl-label"
              value={templateId}
              label="Email Template *"
              onChange={(e) => setTemplateId(e.target.value)}
            >
              {templates.map((tpl) => (
                <MenuItem key={tpl.id} value={tpl.id}>
                  {tpl.name} - {tpl.subject}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="email-to-label">Recipient Destination</InputLabel>
            <Select
              labelId="email-to-label"
              value={toSource}
              label="Recipient Destination"
              onChange={(e) => setToSource(e.target.value)}
            >
              <MenuItem value="contact_email">Enrolled Contact Email (Default)</MenuItem>
              <MenuItem value="variable">Context Variable</MenuItem>
              <MenuItem value="literal">Specific Email Address</MenuItem>
              <MenuItem value="account_users">Internal Account Users</MenuItem>
            </Select>
          </FormControl>

          {toSource === "variable" && (
            <TextField
              fullWidth
              size="small"
              label="Variable Key (e.g. context.agent_email)"
              value={toVariable}
              onChange={(e) => setToVariable(e.target.value)}
            />
          )}

          {toSource === "literal" && (
            <TextField
              fullWidth
              size="small"
              label="Email Address(es)"
              value={toLiteral}
              onChange={(e) => setToLiteral(e.target.value)}
              placeholder="user@example.com"
            />
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-zinc-700">
          <Button variant="outlined" onClick={() => handleClose(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!templateId}
            style={{ backgroundColor: "#f59e0b" }}
          >
            {isEdit ? "Update Node" : "Add Node"}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export const SendEmailNode = (props) => {
  const { data, selected, onEdit, onAdd, onDeleteNode, edges = [], ...rest } = props;
  const { darkMode } = useTheme();

  const cfg = data?.config || {};
  const templateName = data?.templateName || (cfg.templateId ? `Template #${cfg.templateId}` : "Select Template");

  const branches = [
    { key: "sent", label: "Email Sent", color: "bg-emerald-500" },
    { key: "failed", label: "Failed / Bounced", color: "bg-rose-500" },
    { key: "next", label: "Next", color: "bg-slate-400" },
  ];

  const isBranchConnected = (branchKey) =>
    edges.some((e) => e.source === rest.id && (e.sourceHandle === branchKey || (!e.sourceHandle && branchKey === "next")));

  return (
    <div
      className={`w-60 shadow-lg rounded-xl overflow-hidden transition-all duration-200 ${
        darkMode ? "bg-[#18181b] border border-zinc-700" : "bg-white border border-slate-200"
      } ${
        selected ? "ring-2 ring-amber-500 shadow-amber-500/20" : "hover:border-amber-400"
      }`}
    >
      <Handle id="target" type="target" position={Position.Left} />

      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white">
        <div className="flex items-center gap-1.5 min-w-0">
          <EmailIcon style={{ fontSize: 16 }} />
          <span className="text-xs font-semibold tracking-wide uppercase truncate">
            Send Email
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip title="Edit Node">
            <EditIcon
              style={{ fontSize: 14, cursor: "pointer" }}
              onClick={() => onEdit(props)}
            />
          </Tooltip>
          {onDeleteNode && rest.id !== "1" && (
            <Tooltip title="Delete Node">
              <DeleteOutlineIcon
                style={{ fontSize: 15, cursor: "pointer" }}
                onClick={() => onDeleteNode(rest.id)}
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="p-2.5">
        <div className={`text-xs font-medium truncate ${darkMode ? "text-zinc-100" : "text-slate-800"}`}>
          {templateName}
        </div>
        <div className={`text-[10px] mt-1 ${darkMode ? "text-zinc-400" : "text-slate-400"}`}>
          To: {cfg.toSource || "contact_email"}
        </div>
      </div>

      <div className={`border-t ${darkMode ? "border-zinc-800 bg-zinc-900/50" : "border-slate-100 bg-slate-50/50"} px-2.5 py-2 space-y-1.5`}>
        <div className="text-[10px] font-medium uppercase tracking-wider text-slate-400 mb-1">
          Email Result
        </div>
        {branches.map((b) => {
          const connected = isBranchConnected(b.key);
          return (
            <div key={b.key} className="flex items-center justify-between relative py-0.5">
              <div className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${b.color}`} />
                <span className={`text-[11px] font-medium ${darkMode ? "text-zinc-300" : "text-slate-600"}`}>
                  {b.label}
                </span>
              </div>
              <HandleDot
                addVisible={!connected}
                id={b.key}
                type="source"
                position={Position.Right}
                onAdd={(v) =>
                  onAdd(v, rest, rest.id, {
                    type: "SEND_EMAIL",
                    key: b.key,
                  })
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

